import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

const SocialButton = ({ image, text, color1, color2, url, spotify, userId, theme }) => {
  const [spotifyData, setSpotifyData] = useState(null);
  const [isHovered, setIsHovered] = useState(false);

  useEffect(() => {
    let intervalId;
    const fetchSpotifyData = async () => {
      if (spotify) {
        try {
          const response = await fetch(`https://api.lanyard.rest/v1/users/${userId}`);
          const data = await response.json();
          if (data.success && data.data.listening_to_spotify && data.data.spotify) {
            setSpotifyData(data.data.spotify);
          }
        } catch (error) {
          console.error('Error fetching Spotify data:', error);
        }
      }
    };

    if (spotify) {
      fetchSpotifyData();
      intervalId = setInterval(fetchSpotifyData, 5000);
    }

    return () => {
      if (intervalId) clearInterval(intervalId);
    };
  }, [spotify, userId]);

  const spotifyUrl = spotifyData ? `https://open.spotify.com/track/${spotifyData.track_id}` : url;
  const displayText = spotifyData ? `Now Playing: ${spotifyData.song} - ${spotifyData.artist}` : text;
  const displayImage = spotifyData ? spotifyData.album_art_url : image;

  return (
    <a
      href={spotifyUrl}
      target="_blank"
      rel="noopener noreferrer"
      className="relative flex items-center p-3 transition-all duration-300 group overflow-hidden"
      style={{
        background: isHovered ? color1 : 'rgba(0, 0, 0, 0.6)',
        borderBottom: '1px solid',
        borderColor: isHovered ? color1 : 'rgba(249, 115, 22, 0.5)',
        boxShadow: isHovered ? `0 0 15px ${color1}80` : 'none',
        transform: isHovered ? 'scale(1.03)' : 'scale(1)',
      }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <div className="absolute left-0 top-0 bottom-0 w-1 bg-orange-600 opacity-0 group-hover:opacity-100 transition-opacity"></div>
      <img
        src={displayImage}
        alt="Icon"
        className="w-10 h-10 rounded-full mr-3 transition-transform duration-300 group-hover:scale-110 shadow-lg"
      />
      <span className={`text-base font-mono tracking-wide transition-all duration-300 ${isHovered ? 'text-black' : 'text-white'}`}>
        {displayText}
      </span>
    </a>
  );
};

const AboutPage = ({ theme }) => {
  return (
    <div className="relative w-full min-h-screen overflow-hidden">
      {/* Backdrop filter for background video blur */}
      <div className="absolute inset-0 backdrop-blur-sm z-0"></div>
      
      {/* Top Status Bar */}
      <div className="absolute top-0 left-0 right-0 flex justify-between items-center p-2 z-30">
        <div className="flex items-center space-x-1 bg-black bg-opacity-70 border-l-2 border-orange-600 px-2 py-1">
          <div className="h-2 w-2 rounded-full bg-red-500 animate-pulse"></div>
          <span className="text-xs text-white font-bold uppercase">About Page</span>
        </div>
        
        <div className="text-gray-400 text-xs bg-black bg-opacity-70 px-2 py-1">
          {new Date().toLocaleString()}
        </div>
      </div>

      <div className="w-full h-screen flex items-center justify-center">
        <div className="w-full max-w-6xl mx-auto px-4 relative z-10">
          <div className="p-4 sm:p-6 md:p-8 text-center relative z-10">
            <div className="flex flex-col items-stretch gap-8">
            {/* LINKS Section */}
            <div className="mb-6">
              <div className="relative w-full max-w-lg mx-auto mb-6">
                <div className="absolute -left-2 top-1/2 h-px w-6 bg-orange-600"></div>
                <div className="absolute -left-4 top-1/2 h-3 w-3 bg-white"></div>
                <h6 className="text-4xl md:text-5xl lg:text-6xl m-0 font-mono text-white">
                  LINKS_
                </h6>
                <div className="absolute -right-2 top-1/2 h-px w-6 bg-orange-600"></div>
              </div>
              
              <div className="w-full h-px bg-orange-600 mb-4 opacity-50 max-w-3xl mx-auto"></div>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 my-4 max-w-3xl mx-auto">
                <SocialButton
                  theme={theme}
                  image="https://cdn.spookytools.com/assets/github.svg"
                  text="Github"
                  color1="#4078c0"
                  color2="#24292e"
                  url="https://github.com/Spooks4576"
                />
                <SocialButton
                  theme={theme}
                  image="https://cdn.spookytools.com/assets/discord.svg"
                  text="Discord"
                  color1="#7289da"
                  color2="#5865f2"
                  url="https://discord.com/users/985715567463915531/"
                />
                <SocialButton
                  theme={theme}
                  image="https://storage.ko-fi.com/cdn/useruploads/display/aab9d56e-b1a0-407c-8c37-5600fc8ab906_ko-ficircle.png"
                  text="Ko-Fi"
                  color1="#FF5E5B"
                  color2="#434B57"
                  url="https://ko-fi.com/spooky4576"
                />
                <SocialButton
                  theme={theme}
                  image="https://upload.wikimedia.org/wikipedia/commons/8/84/Spotify_icon.svg"
                  text="Not Listening to Anything"
                  color1="#1DB954"
                  color2="#191414"
                  url="#"
                  spotify
                  userId="985715567463915531"
                />
              </div>
              
              <div className="w-full h-px bg-orange-600 mt-4 opacity-50 max-w-3xl mx-auto"></div>
            </div>
            
            {/* PROJECTS Section */}
            <div className="mb-6">
              <div className="relative w-full max-w-lg mx-auto mb-6">
                <div className="absolute -left-2 top-1/2 h-px w-6 bg-orange-600"></div>
                <div className="absolute -left-4 top-1/2 h-3 w-3 bg-white"></div>
                <h6 className="text-4xl md:text-5xl lg:text-6xl m-0 font-mono text-white">
                  PROJECTS_
                </h6>
                <div className="absolute -right-2 top-1/2 h-px w-6 bg-orange-600"></div>
              </div>
              
              <div className="w-full h-px bg-orange-600 mb-4 opacity-50 max-w-3xl mx-auto"></div>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 my-4 max-w-3xl mx-auto">
                <SocialButton
                  theme={theme}
                  image="https://cdn.spookytools.com/assets/trump.png"
                  text="CHINA"
                  color1="#8B0000"
                  color2="#B22222"
                  url="https://china.spookytools.com"
                />
                <SocialButton
                  theme={theme}
                  image="https://cdn.4fingerstudios.com/assets/images/4FSLogoSocials.png"
                  text="4FingerStudios Developer"
                  color1="#7F00FF"
                  color2="#E100FF"
                  url="https://4fingerstudios.com/user-profile/spooky"
                />
              </div>
              
              <div className="w-full h-px bg-orange-600 mt-4 opacity-50 max-w-3xl mx-auto"></div>
            </div>
          </div>
        </div>
      </div>
    </div>
      
      {/* Back Button */}
      <Link 
        to="/" 
        className="absolute bottom-4 left-4 sm:bottom-8 sm:left-8 z-20 flex items-center bg-black bg-opacity-50 border-l-2 border-orange-600 px-3 py-2 group hover:bg-orange-500 transition-all duration-200 hover:border-orange-300 hover:shadow-[0_0_15px_rgba(249,115,22,0.6)]"
      >
        <svg 
          xmlns="http://www.w3.org/2000/svg" 
          className="h-4 w-4 mr-2 text-white group-hover:text-black transition-all duration-200" 
          fill="none" 
          viewBox="0 0 24 24" 
          stroke="currentColor"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
        </svg>
        <span className="text-white text-sm font-bold tracking-wider uppercase group-hover:text-black transition-all duration-200">
          Back to Main Menu
        </span>
      </Link>
      
      {/* Status Info */}
      <div className="fixed bottom-2 right-2 text-gray-500 text-[10px] bg-black bg-opacity-70 p-1 rounded z-30">
        Connection Status: <span className="text-green-500">Connected</span>
      </div>
      
      <style jsx>{`
        @keyframes float {
          0% { transform: translateY(0px); }
          50% { transform: translateY(-8px); }
          100% { transform: translateY(0px); }
        }
      `}</style>
    </div>
  );
};

export default AboutPage;
